import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Loader2, Pause, Play, Trophy } from "lucide-react";
import { toast } from "sonner";
import { Cover } from "./Cover";
import { usePlayer } from "../context/PlayerContext";
import { errMsg, http, mediaUrl } from "../lib/api";
import { engineCost, ENGINES, findItem, fmtUsd, STATUS } from "../lib/presets";

const fmtTime = (s) => (s ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}` : "–");

const CompareCard = ({ s, list, isCurrent, onPick }) => {
  const p = usePlayer();
  const [dur, setDur] = useState(0);
  const e = findItem(ENGINES, s.engine);
  const done = s.status === "completed";
  const on = p.current?.id === s.id && p.playing;
  return (
    <div data-testid={`compare-card-${s.id}`}
      className={`relative p-4 rounded-2xl border transition-colors ${s.compare_winner ? "border-[var(--c-orange)] bg-[var(--c-orange)]/5" : "border-[var(--c-line)] bg-[var(--c-surface)]"}`}>
      {s.compare_winner && (
        <span data-testid={`compare-winner-${s.id}`} className="absolute -top-3 left-4 flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[var(--c-orange)] text-white text-[11px] font-semibold">
          <Crown className="w-3 h-3" />Laimėtoja
        </span>
      )}
      <div className="flex gap-4">
        <Cover song={s} className="w-20 h-20 rounded-xl shrink-0" />
        <div className="min-w-0">
          <p className="font-semibold">{e.name}</p>
          <p className="text-xs text-zinc-500">{e.tag}</p>
          <p className="text-xs font-mono text-zinc-400 mt-2">
            {done ? <>Trukmė <span data-testid={`compare-duration-${s.id}`}>{fmtTime(dur)}</span></> : STATUS[s.status]?.label || s.status}
            {" · "}<span className="text-emerald-400">{fmtUsd(engineCost(s.engine, s.length))}</span>
          </p>
          {s.status === "failed" && <p className="text-xs text-red-400 mt-1">{s.error}</p>}
        </div>
      </div>
      {done && <audio src={mediaUrl(s.audio_path)} preload="metadata" onLoadedMetadata={(ev) => setDur(ev.target.duration)} className="hidden" />}
      <div className="flex flex-wrap gap-2 mt-4">
        <button data-testid={`compare-play-${s.id}`} disabled={!done} onClick={() => p.play(s, list)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-black text-sm font-semibold hover:bg-zinc-200 transition-colors disabled:opacity-40">
          {on ? <Pause className="w-3.5 h-3.5" fill="currentColor" /> : <Play className="w-3.5 h-3.5" fill="currentColor" />}{on ? "Pauzė" : "Groti"}
        </button>
        {done && s.is_mine && !s.compare_winner && (
          <button data-testid={`compare-pick-${s.id}`} onClick={() => onPick(s)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-[var(--c-line)] text-sm hover:border-[var(--c-orange)] hover:text-[var(--c-orange)] transition-colors">
            <Trophy className="w-3.5 h-3.5" />Pasirinkti geresnę
          </button>
        )}
        {!isCurrent && <Link to={`/daina/${s.id}`} data-testid={`compare-open-${s.id}`} className="px-4 py-2 text-sm text-zinc-400 hover:text-white transition-colors">Atidaryti</Link>}
      </div>
    </div>
  );
};

export const EngineCompare = ({ song }) => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => http.get(`/songs/${song.id}/compare`).then((r) => setItems(r.data)), [song.id]);
  useEffect(() => { load(); }, [load]);
  const pending = items.some((s) => !["completed", "failed"].includes(s.status));
  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [pending, load]);
  const pick = async (s) => {
    try { setItems((await http.post(`/songs/${s.id}/winner`)).data); toast.success("Laimėtoja pažymėta"); } catch (e) { toast.error(errMsg(e)); }
  };
  if (items.length < 2) return null;
  const list = items.filter((s) => s.status === "completed");
  return (
    <section data-testid="engine-compare" className="mt-10">
      <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-4">
        Variklių palyginimas {pending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((s) => <CompareCard key={s.id} s={s} list={list} isCurrent={s.id === song.id} onPick={pick} />)}
      </div>
    </section>
  );
};
